import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Trash2, Edit3, RefreshCw, X, UploadCloud, Image as ImageIcon, Loader2,
  Sparkles, Zap, AlertCircle, Wand2, CalendarClock,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, formatDate } from './shared';
import { useToast } from '../../hooks/use-toast';
import RichTextEditor from '../../components/RichTextEditor';
import AiImageButton from './AiImageButton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BLANK = {
  title: '', slug: '', excerpt: '', content: '', cover_image: '',
  tags: '', published: true, publish_at: null, seo_title: '', seo_description: '',
};

function slugify(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').substring(0, 120);
}

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in LOCAL time;
// we store ISO UTC. These two helpers bridge the gap.
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function AdminBlog() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/blog');
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: 'Failed to load posts', description: formatApiError(err) });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const onNew = () => setEditing({ ...BLANK });

  const onEdit = async (id) => {
    try {
      const res = await api.get(`/api/admin/blog/${id}`);
      setEditing({ ...res.data, tags: (res.data.tags || []).join(', ') });
    } catch (err) { toast({ title: 'Load failed', description: formatApiError(err) }); }
  };

  const save = async () => {
    if (!editing.title?.trim()) return toast({ title: 'Title required' });
    const slug = editing.slug?.trim() || slugify(editing.title);
    const payload = {
      title: editing.title.trim(),
      slug,
      excerpt: editing.excerpt || '',
      content: editing.content || '',
      cover_image: editing.cover_image || '',
      tags: (typeof editing.tags === 'string' ? editing.tags.split(',') : editing.tags || []).map((t) => t.trim()).filter(Boolean),
      published: !!editing.published,
      publish_at: editing.publish_at || null,
      seo_title: editing.seo_title || '',
      seo_description: editing.seo_description || '',
    };
    setSaving(true);
    try {
      if (editing.id) await api.patch(`/api/admin/blog/${editing.id}`, payload);
      else await api.post('/api/admin/blog', payload);
      toast({ title: 'Saved' });
      setEditing(null);
      load();
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/api/admin/blog/${id}`);
      toast({ title: 'Deleted' });
      load();
    } catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('folder', 'blog');
      const res = await api.post('/api/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = BACKEND + res.data.url;
      setEditing((e) => ({ ...e, cover_image: url }));
      toast({ title: 'Image uploaded' });
    } catch (err) { toast({ title: 'Upload failed', description: formatApiError(err) }); }
    finally { setUploading(false); }
  };

  // ----- AI generation: single -----
  const aiGenerate = async ({ topic, keywords, word_count, save: doSave }) => {
    setAiBusy(true);
    try {
      const res = await api.post('/api/admin/blog/generate', {
        topic, keywords, word_count, save: !!doSave,
      });
      if (doSave) {
        toast({ title: 'AI post created and saved' });
        load();
        setGenOpen(false);
      } else {
        // Pre-fill the editor with AI output
        setEditing({
          ...res.data,
          tags: (res.data.tags || []).join(', '),
        });
        setGenOpen(false);
        toast({ title: 'AI draft generated', description: 'Review and click Save when ready.' });
      }
    } catch (err) {
      toast({ title: 'AI generation failed', description: formatApiError(err) });
    } finally { setAiBusy(false); }
  };

  // ----- AI generation: bulk -----
  const aiBulk = async ({ mode, limit, publish }) => {
    setAiBusy(true);
    try {
      // Small batches run synchronously; larger ones go through the async/job pipeline
      // to avoid the public ingress's ~60s timeout.
      if (limit > 0 && limit <= 3) {
        const res = await api.post('/api/admin/blog/bulk-generate', { mode, limit, publish }, { timeout: 240000 });
        const r = res.data;
        toast({
          title: 'Bulk generation complete',
          description: `${r.created.length} created · ${r.skipped.length} skipped · ${r.errors.length} errors`,
        });
        setBulkOpen(false);
        load();
      } else {
        const res = await api.post('/api/admin/blog/bulk-generate-async', { mode, limit, publish });
        const { job_id, total } = res.data;
        toast({ title: 'Bulk job started', description: `Generating up to ${total} posts in the background…` });
        setBulkOpen(false);
        // Poll progress
        const interval = setInterval(async () => {
          try {
            const j = await api.get(`/api/admin/blog/jobs/${job_id}`);
            if (j.data.status !== 'running') {
              clearInterval(interval);
              toast({
                title: `Job ${j.data.status}`,
                description: `${j.data.created} created · ${j.data.skipped} skipped · ${j.data.errors} errors`,
              });
              load();
            }
          } catch (_) {
            clearInterval(interval);
          }
        }, 4000);
      }
    } catch (err) {
      toast({ title: 'Bulk failed', description: formatApiError(err) });
    } finally { setAiBusy(false); }
  };

  // ----- AI: backfill cover images for existing posts -----
  const backfillCovers = async () => {
    if (!window.confirm('Pick a content-relevant cover image for every blog post that is currently missing one?')) return;
    setAiBusy(true);
    try {
      const res = await api.post('/api/admin/blog/backfill-covers?only_missing=true');
      toast({ title: 'Covers updated', description: `${res.data.updated} updated · ${res.data.skipped} skipped` });
      load();
    } catch (err) {
      toast({ title: 'Backfill failed', description: formatApiError(err) });
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Blog Posts"
        subtitle="Create manually, generate with Claude AI, or batch-create across all services & countries"
        action={
          <div className="flex flex-wrap gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn variant="ghost" onClick={backfillCovers} disabled={aiBusy} testId="blog-backfill-btn"><ImageIcon size={14} /> Fix covers</Btn>
            <Btn variant="ghost" onClick={() => setBulkOpen(true)} testId="blog-bulk-btn"><Zap size={14} /> Bulk AI</Btn>
            <Btn onClick={() => setGenOpen(true)} testId="blog-ai-btn">
              <Sparkles size={14} /> AI Generate
            </Btn>
            <Btn onClick={onNew} testId="blog-new-btn"><Plus size={14} /> New post</Btn>
          </div>
        }
      />

      <AdminCard>
        {loading ? <Loading /> : items.length === 0 ? <Empty>No posts yet. Click <b>AI Generate</b> for an instant draft, <b>Bulk AI</b> for a full content pack, or <b>New post</b> to write manually.</Empty> : (
          <div className="divide-y divide-white/5">
            {items.map((p) => (
              <div key={p.id} className="py-4 flex items-start gap-4" data-testid={`blog-row-${p.id}`}>
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-800 flex-shrink-0 flex items-center justify-center">
                  {p.cover_image ? <img src={p.cover_image} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-slate-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white truncate">{p.title}</p>
                  <p className="text-xs text-slate-400 truncate">/{p.slug}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatDate(p.published_at)} ·{' '}
                    {p.publish_at && new Date(p.publish_at) > new Date() ? (
                      <span className="text-sky-300 inline-flex items-center gap-1"><CalendarClock size={11} /> Scheduled {new Date(p.publish_at).toLocaleString()}</span>
                    ) : p.published ? (
                      <span className="text-emerald-300">Published</span>
                    ) : (
                      <span className="text-amber-300">Draft</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Btn size="sm" variant="ghost" onClick={() => onEdit(p.id)} testId={`blog-edit-${p.id}`}><Edit3 size={12} /> Edit</Btn>
                  <Btn size="sm" variant="danger" onClick={() => remove(p.id)} testId={`blog-delete-${p.id}`}><Trash2 size={12} /></Btn>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminCard>

      {/* ===== Editor drawer ===== */}
      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside
            className="w-full max-w-3xl bg-slate-900 border-l border-white/10 p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="blog-editor"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">{editing.id ? 'Edit post' : 'New post'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <Input label="Title *" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="blog-title" />
              <Input label="Slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto-generated from title" />
              <div>
                <span className="text-xs font-semibold text-slate-300 mb-1.5 block">Excerpt</span>
                <textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 outline-none text-white text-sm resize-none" />
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-300 mb-1.5 block">Content</span>
                <RichTextEditor
                  value={editing.content}
                  onChange={(v) => setEditing({ ...editing, content: v })}
                  placeholder="Write your post in HTML, or click 'Re-generate' below to use Claude AI."
                />
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-300 mb-1.5 block">Cover image</span>
                {editing.cover_image && (
                  <img src={editing.cover_image} alt="cover" className="w-full h-40 object-cover rounded-lg mb-2 border border-white/10" />
                )}
                <div className="flex flex-wrap gap-2">
                  <Input value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} placeholder="https://… or upload below" className="flex-1 min-w-[200px]" />
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-200 hover:bg-white/10 cursor-pointer">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload
                    <input type="file" accept="image/*" hidden onChange={(e) => uploadCover(e.target.files?.[0])} />
                  </label>
                  <AiImageButton
                    defaultPrompt={editing.title ? `Wide blog cover image for: "${editing.title}". Professional, marketing-quality, no text, business / telecoms theme.` : ''}
                    folder="blog"
                    onGenerated={(url) => setEditing((e) => ({ ...e, cover_image: url }))}
                    label="Generate with AI"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Tip: use the <strong>Fix covers</strong> button above to bulk-fill missing covers for every published post.
                </p>
              </div>
              <Input label="Tags (comma separated)" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
              <div className="grid sm:grid-cols-2 gap-3">
                <Input label="SEO Title" value={editing.seo_title} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} />
                <Input label="SEO Description" value={editing.seo_description} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} />
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-4 h-4 accent-fuchsia-500" />
                <span className="text-sm text-slate-300">Published</span>
              </label>

              {/* Scheduled publish */}
              <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarClock size={14} className="text-violet-300" />
                  <span className="text-sm font-semibold text-slate-200">Schedule publish (optional)</span>
                </div>
                <input
                  type="datetime-local"
                  value={toLocalInput(editing.publish_at)}
                  onChange={(e) => setEditing({ ...editing, publish_at: fromLocalInput(e.target.value) })}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 outline-none text-white text-sm"
                />
                <p className="text-[11px] text-slate-400 mt-1.5">
                  {editing.publish_at
                    ? `Hidden from the public site until ${new Date(editing.publish_at).toLocaleString()}. Keep "Published" ticked.`
                    : 'Leave empty to publish immediately. Set a future date + time to schedule.'}
                </p>
                {editing.publish_at && (
                  <button type="button" onClick={() => setEditing({ ...editing, publish_at: null })} className="text-[11px] text-rose-300 hover:text-rose-200 mt-1">
                    Clear schedule
                  </button>
                )}
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving} testId="blog-save-btn">
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save'}
              </Btn>
            </div>
          </aside>
        </div>
      )}

      {/* ===== AI Single Generate dialog ===== */}
      {genOpen && <AIGenerateDialog busy={aiBusy} onClose={() => setGenOpen(false)} onGenerate={aiGenerate} />}

      {/* ===== AI Bulk dialog ===== */}
      {bulkOpen && <AIBulkDialog busy={aiBusy} onClose={() => setBulkOpen(false)} onRun={aiBulk} />}
    </>
  );
}

/* ----------------- Sub-components ----------------- */

function AIGenerateDialog({ busy, onClose, onGenerate }) {
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [wordCount, setWordCount] = useState(800);
  const [autoSave, setAutoSave] = useState(false);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!topic.trim()) return;
    onGenerate({
      topic: topic.trim(),
      keywords: keywords.split(',').map((s) => s.trim()).filter(Boolean),
      word_count: Number(wordCount) || 800,
      save: autoSave,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-slate-900 border border-violet-500/30 rounded-2xl p-6 w-full max-w-lg shadow-2xl shadow-violet-500/20"
        data-testid="ai-generate-dialog"
      >
        <div className="flex items-center gap-2 mb-3">
          <Wand2 size={18} className="text-fuchsia-400" />
          <h3 className="text-xl font-bold text-white">Generate with Claude AI</h3>
        </div>
        <p className="text-sm text-slate-400 mb-4">Claude Sonnet 4.5 will write a full blog post in British English, optimised for the UK market.</p>
        <div className="space-y-3">
          <Input
            label="Topic *"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Why UK retailers should adopt cloud telephony in 2026"
            data-testid="ai-topic"
            autoFocus
          />
          <Input label="Target keywords (comma separated)" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="cloud telephony, retail voip, uk smb" />
          <Input label="Approximate word count" type="number" value={wordCount} onChange={(e) => setWordCount(e.target.value)} min="300" max="2000" />
          <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={autoSave} onChange={(e) => setAutoSave(e.target.checked)} className="mt-1 w-4 h-4 accent-fuchsia-500" />
            <span>Save & publish immediately (otherwise the editor opens with the AI draft)</span>
          </label>
        </div>
        <div className="mt-5 pt-4 border-t border-white/10 flex gap-2 justify-end">
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn type="submit" disabled={busy || !topic.trim()} testId="ai-submit">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate</>}
          </Btn>
        </div>
      </form>
    </div>
  );
}

function AIBulkDialog({ busy, onClose, onRun }) {
  const [mode, setMode] = useState('services');
  const [limit, setLimit] = useState(0);
  const [publish, setPublish] = useState(true);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-slate-900 border border-amber-500/40 rounded-2xl p-6 w-full max-w-lg shadow-2xl shadow-amber-500/10">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={18} className="text-amber-400" />
          <h3 className="text-xl font-bold text-white">Bulk AI generation</h3>
        </div>
        <p className="text-sm text-slate-400 mb-4">Pick a content pack — Claude will generate one post per item. Skipped if a post already exists for that subject.</p>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('services')}
              className={`p-4 rounded-xl border text-left transition ${mode === 'services'
                ? 'bg-violet-500/15 border-violet-500/60 text-white'
                : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'}`}
              data-testid="bulk-mode-services"
            >
              <p className="text-sm font-bold">All Services</p>
              <p className="text-xs text-slate-400 mt-0.5">14 VoIPinger services (VoIP, SIP, MS Teams, Contact Centre, etc.)</p>
            </button>
            <button
              type="button"
              onClick={() => setMode('countries')}
              className={`p-4 rounded-xl border text-left transition ${mode === 'countries'
                ? 'bg-violet-500/15 border-violet-500/60 text-white'
                : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'}`}
              data-testid="bulk-mode-countries"
            >
              <p className="text-sm font-bold">All DID Countries</p>
              <p className="text-xs text-slate-400 mt-0.5">"How to get a virtual number in [Country]" guide for every DID country in the database</p>
            </button>
          </div>
          <Input label="Limit (1-50, 0 = all)" type="number" value={limit} onChange={(e) => setLimit(e.target.value)} min="0" max="50" />
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="w-4 h-4 accent-fuchsia-500" />
            <span>Publish immediately (otherwise saved as drafts)</span>
          </label>
        </div>

        <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2 text-xs text-amber-200">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            Each post takes 10–20 seconds. Limits ≤ 3 run instantly; larger batches run in
            the background and you'll see progress notifications. Max per run: 50.
          </span>
        </div>

        <div className="mt-5 pt-4 border-t border-white/10 flex gap-2 justify-end">
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn onClick={() => onRun({ mode, limit: Number(limit) || 0, publish })} disabled={busy} testId="bulk-run">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Running…</> : <><Zap size={14} /> Run bulk</>}
          </Btn>
        </div>
      </div>
    </div>
  );
}
